
import React from 'react';
import { Progress } from '@/components/ui/progress';
import { Loader, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';

interface LoadingStateProps {
  analysisBatch?: {
    total: number;
    completed: number;
    current: string;
  };
  progress?: number;
  stage?: string;
  pagesProcessed?: number;
  totalPages?: number;
  errorRate?: number;
  successRate?: number;
}

export const LoadingState: React.FC<LoadingStateProps> = ({ 
  analysisBatch,
  progress,
  stage,
  pagesProcessed,
  totalPages,
  errorRate,
  successRate
}) => {
  const [showDetails, setShowDetails] = React.useState(false);

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  // Format progress to make sure it never exceeds 100%
  const formattedProgress = progress ? Math.min(progress, 100) : 0;

  return (
    <div className="neo-card p-6">
      <div className="flex flex-col items-center justify-center py-6">
        <div className="relative w-12 h-12 mb-4">
          <div className="absolute inset-0 rounded-full border-2 border-secondary border-t-primary border-r-primary animate-spin-slow"></div>
          <Loader className="w-6 h-6 text-primary absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
        </div>
        
        {analysisBatch ? (
          <div className="w-full max-w-md">
            <p className="text-center mb-2">
              Analyzing page {analysisBatch.completed + 1} of {analysisBatch.total}
            </p>
            <Progress 
              value={(analysisBatch.completed / analysisBatch.total) * 100} 
              className="h-2 mb-2 bg-secondary/50" 
              indicatorClassName="bg-gradient-to-r from-indigo-500 to-purple-600"
              showValue
              valueFormatter={(value) => `${Math.round(value)}%`}
            />
            <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
              <p className="truncate max-w-[300px]">
                Current: {analysisBatch.current}
              </p>
              <button 
                onClick={toggleDetails}
                className="ml-2 p-1 rounded-full bg-secondary/30 hover:bg-secondary/50 transition-colors"
                aria-label={showDetails ? "Hide details" : "Show details"}
              >
                {showDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>
            
            {showDetails && (
              <div className="mt-4 p-3 bg-secondary/20 rounded-md text-xs max-h-40 overflow-y-auto">
                <p className="text-muted-foreground mb-1">Processing queue:</p>
                <ul className="space-y-1">
                  <li className="text-primary">• {analysisBatch.current}</li>
                  {/* Example of what could be shown here */}
                  <li className="text-muted-foreground">• Next pages in queue...</li>
                </ul>
              </div>
            )}
          </div>
        ) : progress !== undefined && stage ? (
          <div className="w-full max-w-md">
            <p className="text-center mb-2">{stage}</p>
            <Progress 
              value={formattedProgress} 
              className="h-2 mb-2 bg-secondary/50" 
              indicatorClassName="bg-gradient-to-r from-indigo-500 to-purple-600"
              showValue
              valueFormatter={(value) => `${Math.round(value)}%`}
            />
            {pagesProcessed !== undefined && totalPages !== undefined && (
              <div className="flex justify-between items-center text-xs text-muted-foreground mt-1">
                <span>{pagesProcessed} of {totalPages} pages processed</span>
                {(errorRate !== undefined || successRate !== undefined) && (
                  <div className="flex items-center gap-2">
                    {successRate !== undefined && (
                      <span className="text-green-500">{Math.round(successRate)}% success</span>
                    )}
                    {errorRate !== undefined && errorRate > 0 && (
                      <span className="flex items-center text-amber-500">
                        <AlertCircle size={12} className="inline mr-1" />
                        {Math.round(errorRate)}% errors
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
            {showDetails && (
              <div className="mt-4 p-3 bg-secondary/20 rounded-md text-xs max-h-40 overflow-y-auto">
                <p className="text-muted-foreground mb-1">Crawler progress details:</p>
                <ul className="space-y-1">
                  <li>• Stage: {stage}</li>
                  <li>• Pages processed: {pagesProcessed} / {totalPages}</li>
                  <li>• Progress: {formattedProgress.toFixed(1)}%</li>
                </ul>
              </div>
            )}
            <div className="flex justify-center mt-2">
              <button 
                onClick={toggleDetails}
                className="flex items-center text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                {showDetails ? "Hide details" : "Show details"}
                {showDetails ? <ChevronUp size={14} className="ml-1" /> : <ChevronDown size={14} className="ml-1" />}
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-foreground">Fetching page content and analyzing...</p>
            <p className="text-xs text-muted-foreground mt-1">This may take up to 30 seconds</p>
          </>
        )}
      </div>
    </div>
  );
};
